import type { FC } from "react";

import { getImageProps } from "next/image";

import { breakpoints } from "@jti/ui/hooks/useDeviceBreakpoint/useDeviceBreakpoint";

import type { responsiveImageInputs } from "./ResponsiveImage.builder";

import styles from "./ResponsiveImage.module.scss";

export type ResponsiveImageProps = BuilderComponentProps<typeof responsiveImageInputs>;

const getOptionalImageProps = (
  builderImage: {
    image?: string;
    width?: number;
    height?: number;
  },
  quality: number,
  alt: string
) => {
  if (builderImage.image && builderImage.width && builderImage.height) {
    return getImageProps({
      alt,
      width: builderImage.width,
      height: builderImage.height,
      quality,
      src: builderImage.image,
    });
  }
  return undefined;
};

export const ResponsiveImage: FC<ResponsiveImageProps> = ({
  desktopImage,
  tabletImage,
  mobileImage,
  altText,
  title,
  highPriority,
  quality = 80,
}) => {
  const desktopImageProps = getOptionalImageProps(desktopImage, quality, altText);
  const tabletImageProps = getOptionalImageProps(tabletImage, quality, altText);
  const mobileImageProps = getOptionalImageProps(mobileImage, quality, altText);

  const imageReservedHeightStyles = {
    "--responsive-image-desktop-aspect-ratio": desktopImage.aspectRatio,
    "--responsive-image-tablet-aspect-ratio": tabletImage.aspectRatio || desktopImage.aspectRatio,
    "--responsive-image-mobile-aspect-ratio":
      mobileImage.aspectRatio || tabletImage.aspectRatio || desktopImage.aspectRatio,
  };

  const fallbackImageProps = mobileImageProps || tabletImageProps || desktopImageProps;

  return (
    <picture style={imageReservedHeightStyles}>
      <source media={breakpoints.desktop} srcSet={desktopImageProps?.props?.srcSet} />
      <source media={breakpoints.tablet} srcSet={tabletImageProps?.props?.srcSet || desktopImageProps?.props?.srcSet} />
      <source
        media={breakpoints.mobile}
        srcSet={mobileImageProps?.props?.srcSet || tabletImageProps?.props?.srcSet || desktopImageProps?.props?.srcSet}
      />
      <img
        {...fallbackImageProps?.props}
        className={styles.image}
        alt={altText}
        title={title}
        loading={highPriority ? "eager" : "lazy"}
      />
    </picture>
  );
};
